import EventBus from 'diagram-js/lib/core/EventBus';
import Modeling from 'diagram-js/lib/features/modeling/Modeling';
import {PathwaysModeling} from '@ispm-client/modules/bpmn-modeler/bpmn/commands/pathways-modeling';
import {Connection, Shape} from 'diagram-js/lib/model';
import {RulesetUtils} from '@ispm-client/modules/bpmn-modeler/utils/ruleset-utils';
import {ModelingUtil} from '@ispm/shared/bpmn-io/modeling-util';
import {
  generateCounterName,
  startGatewayToScriptTaskConn,
  startGatewayToUserTaskConn
} from '@ispm/shared/bpmn-io/sub-process-consts';
import {ScriptTaskGenerator} from '@ispm-client/modules/bpmn-modeler/utils/script-task-generator';
import CommandHandler from 'diagram-js/lib/command/CommandHandler';
import {
  nodeDetailsQuestion,
  nodesDetailsExtensionType,
  scriptTaskType,
  userTaskType
} from '@ispm-shared/modules/bpmn/diagram-element-terms';
import {QueryNode, Rule} from '@ispm-client/modules/query-builder/models/query-builder-config';
import * as _ from 'lodash';
import {uniqBy} from 'lodash';
import {BpmnExtensionHelper} from '@ispm/shared/bpmn-io/bpmn-extension-helper';
import {NodeCreationResult} from '@ispm-client/modules/bpmn-modeler/models/node-creation-result';


export class CalculateNodeChildsDataHandler implements CommandHandler {


  constructor(eventBus: EventBus, private modeling: Modeling, private pathwaysModeling: PathwaysModeling, private moddle,
              private bpmnExtensionHelper: BpmnExtensionHelper,private elementRegistry) {
  }

  public postExecute(context) {
    if (!_.isEmpty(context.nodeCreationResults) && !_.isEmpty(context.parentElement)) {
      this.calculateRulesetDataToParentSubProcess(context.parentElement, context.nodeCreationResults);
    }
  }

  private calculateRulesetDataToParentSubProcess(sourceElement: Shape, nodeCreationResults: Array<NodeCreationResult>) {
    const scriptTask: Shape = ModelingUtil.getScriptAndUserTask(sourceElement, scriptTaskType,this.elementRegistry);
    const userTask: Shape = ModelingUtil.getScriptAndUserTask(sourceElement, userTaskType,this.elementRegistry);
    if (scriptTask && userTask) {
      const scriptConnectionID = `${scriptTask.id}_${startGatewayToScriptTaskConn}`;
      const userConnectionID = `${userTask.id}_${startGatewayToUserTaskConn}`;
      nodeCreationResults.forEach(nodeCreationResult => {
        const childNodeId: string = nodeCreationResult.id;
        const ruleset: QueryNode = nodeCreationResult.ruleset;
        if (ruleset) {
          let condition = RulesetUtils.extractConditionExpression(nodeCreationResult.id, nodeCreationResult.ruleset, true);
          const childNode: Shape = ModelingUtil.getDescendantNodeById(sourceElement, childNodeId);
          const gateway = ModelingUtil.getNodeIncomingGateway(childNode);
          if (gateway) {
            const isComplicatedRule: boolean = RulesetUtils.hasComplicatedRule(nodeCreationResult.ruleset);
            const manualSelectNodeCondition: Rule = RulesetUtils.addManualSelectCondition(nodeCreationResult, nodeCreationResult.id, gateway.id);
            const noManualSelectNodeCondition: string = RulesetUtils.parseRuleToIsEMpty(manualSelectNodeCondition);
            condition = RulesetUtils.wrapConditionWithManualSelectLogic(condition, manualSelectNodeCondition, noManualSelectNodeCondition);
            this.updateSubProcessProperties(sourceElement, childNodeId, ruleset, manualSelectNodeCondition, isComplicatedRule);
            this.updateScriptTaskScript(sourceElement, condition);
          }
        }

      });
      if (nodeCreationResults.length > 1 || nodeCreationResults[0].ruleset) {
        // update script cond
        const startGatewayToScriptTaskConnection: Connection = ModelingUtil.getChildConnectionById(sourceElement, scriptConnectionID,this.elementRegistry);
        this.appendDataExistenceExpressionToConnection(nodeCreationResults, startGatewayToScriptTaskConnection, RulesetUtils.and, RulesetUtils.or, true);

        // update user task cond
        const startGatewayToUserTaskConnection: Connection = ModelingUtil.getChildConnectionById(sourceElement, userConnectionID,this.elementRegistry);
        this.appendDataExistenceExpressionToConnection(nodeCreationResults, startGatewayToUserTaskConnection, RulesetUtils.or, RulesetUtils.and, false);
      }

    } else {
      console.warn("[calculateRulesetDataToParentSubProcess] scriptTask && userTask not found for element id " + sourceElement.id);
    }
  }

  private appendDataExistenceExpression(nodeId: string, ruleset: QueryNode, conditionExpression, logicalOperator: string, exist: boolean): string {
    const newExpression = RulesetUtils.generateDataExistenceConditionExpression(nodeId, ruleset, exist);
    return this.generateCondition(conditionExpression, newExpression, logicalOperator);
  }

  private appendDataExistenceExpressionForRule(nodeId: string, rule: Rule, conditionExpression, logicalOperator: string, exist: boolean): string {
    const newExpression = RulesetUtils.generateDataExistenceConditionExpressionForRule(nodeId, rule, exist);
    return this.generateCondition(conditionExpression, newExpression, logicalOperator);
  }

  private generateCondition(conditionExpression, newExpression, logicalOperator: string) {
    if (!_.isEmpty(conditionExpression)) {
      newExpression = `${conditionExpression} ${logicalOperator} ${newExpression}`;
    }
    return newExpression;
  }

  private appendDataExistenceExpressionToConnection(nodeCreationResults: Array<NodeCreationResult>, connection: Connection, rulesetLogicalOperator: string, manualSelectionLogicalOperator: string, exist: boolean) {
    let newExpression = '';
    nodeCreationResults.forEach(nodeCreationResult => {
      // in case copy hierarchy of head node skip empty condition
      if(nodeCreationResult.ruleset){
        newExpression = this.appendDataExistenceExpression(nodeCreationResult.id, nodeCreationResult.ruleset, newExpression, rulesetLogicalOperator, exist);
      }
    });
    newExpression = `(${newExpression})`;

    // adding the manual selection
    nodeCreationResults.forEach(nodeCreationResult => {
      if (nodeCreationResult.manualSelectNode) {
        newExpression = this.appendDataExistenceExpressionForRule(nodeCreationResult.id, nodeCreationResult.manualSelectNode, newExpression, manualSelectionLogicalOperator, exist);
      }

    });
    this.pathwaysModeling.appendConnectionExpression(connection, newExpression);
  }


  private updateScriptTaskScript(sourceElement: Shape, condition: string) {
    const scriptTask: Shape = ModelingUtil.getScriptAndUserTask(sourceElement, scriptTaskType,this.elementRegistry);
    const scriptTaskGenerator = new ScriptTaskGenerator();
    const updatedScript = scriptTaskGenerator.generateScript(scriptTask.businessObject.script, condition, generateCounterName(sourceElement.id));
    this.modeling.updateProperties(scriptTask, {script: updatedScript});
  }

  private updateSubProcessProperties(sourceElement, nodeId: string, ruleset: QueryNode, manualSelectNode: Rule,
                                     isComplicatedRule: boolean) {
    const nodeDetails = ModelingUtil.getNodeDetails(sourceElement) || this.moddle.create(nodesDetailsExtensionType);
    const questions = nodeDetails.questions || [];
    const fields: Array<string> = [];
    if (manualSelectNode) {
      fields.push(manualSelectNode.fullName);
    }
    if (!isComplicatedRule) {
      fields.push(...RulesetUtils.extractRulesFields(nodeId, ruleset));
    }
    const newQuestions = fields.map(field => this.moddle.create(nodeDetailsQuestion, {key: field}));
    nodeDetails.questions = uniqBy([...questions, ...newQuestions], 'key');
    this.bpmnExtensionHelper.addOrReplaceExtension(sourceElement, nodeDetails);
  }
}
